import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core-accordion';
import '@maersk-global/mds-components-core-accordion-item';
import '@maersk-global/mds-components-core-input';
import '@maersk-global/mds-components-core-notification';
import { VscKpiGroup } from '../../../models/vsc-settings.model';

@Component({
  selector: 'app-vsc-kpi-weights',
  imports: [CommonModule],
  templateUrl: './vsc-kpi-weights.component.html',
  styleUrl: './vsc-kpi-weights.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscKpiWeightsComponent {
  @Input() kpiGroups: VscKpiGroup[] = [];
  @Input() kpiWeights: Record<string, string> = {};
  @Input() categoryWeights: Record<string, string> = {};
  @Input() readonly = false;

  @Output() kpiWeightsChange = new EventEmitter<Record<string, string>>();
  @Output() categoryWeightsChange = new EventEmitter<Record<string, string>>();

  readonly inputWidth = 88;

  get catTotal(): number {
    return this.kpiGroups.reduce((acc, g) => {
      const v = Number.parseFloat(this.categoryWeights[g.id] || '0');
      return acc + (Number.isNaN(v) ? 0 : v);
    }, 0);
  }

  get isCatOver(): boolean {
    return this.catTotal > 100;
  }

  get isCatUnder(): boolean {
    return this.catTotal < 100;
  }

  get isCatExact(): boolean {
    return this.catTotal === 100;
  }

  groupSum(group: VscKpiGroup): number {
    return group.kpis.reduce((acc, kpi) => {
      const v = Number.parseFloat(this.kpiWeights[kpi.id]);
      return acc + (Number.isNaN(v) ? 0 : v);
    }, 0);
  }

  isGroupOver(group: VscKpiGroup): boolean {
    return this.groupSum(group) > (Number.parseFloat(this.categoryWeights[group.id] || '0') || 0);
  }

  isGroupUnder(group: VscKpiGroup): boolean {
    const total = Number.parseFloat(this.categoryWeights[group.id] || '0') || 0;
    return total > 0 && this.groupSum(group) < total;
  }

  isGroupExact(group: VscKpiGroup): boolean {
    const total = Number.parseFloat(this.categoryWeights[group.id] || '0') || 0;
    return total > 0 && this.groupSum(group) === total;
  }

  groupRemaining(group: VscKpiGroup): number {
    const total = Number.parseFloat(this.categoryWeights[group.id] || '0') || 0;
    return total - this.groupSum(group);
  }

  fmt(n: number): string {
    return n % 1 === 0 ? String(n) : n.toFixed(1);
  }

  sumColor(group: VscKpiGroup): string {
    if (this.isGroupOver(group)) {
      return 'var(--mds_brand_appearance_error_default_background-color)';
    }
    if (this.isGroupExact(group)) {
      return 'var(--mds_brand_appearance_success_default_background-color)';
    }
    return 'var(--mds_brand_appearance_neutral_default_on-background-color)';
  }

  groupBoxShadow(group: VscKpiGroup): string {
    return this.isGroupOver(group) || this.isGroupUnder(group)
      ? '0 0 0 1px rgba(200,0,0,0.15), 0 4px 14px rgba(200,0,0,0.25)'
      : '0 2px 8px rgba(0,0,0,0.08)';
  }

  fmtCat(n: number): string {
    return n % 1 === 0 ? String(n) : n.toFixed(1);
  }

  onKpiWeightChange(id: string, event: Event): void {
    if (this.readonly) {
      return;
    }

    const value = (event.target as HTMLInputElement).value;
    if (value === '' || /^(?:\d+(?:\.\d*)?)$/.test(value)) {
      this.kpiWeightsChange.emit({ ...this.kpiWeights, [id]: value });
    }
  }

  onCatWeightChange(id: string, event: Event): void {
    if (this.readonly) {
      return;
    }

    const value = (event.target as HTMLInputElement).value;
    if (value === '' || /^(?:\d+(?:\.\d*)?)$/.test(value)) {
      this.categoryWeightsChange.emit({ ...this.categoryWeights, [id]: value });
    }
  }
}
